import { useId, type CSSProperties, type ReactNode } from 'react';
import { Icons } from 'markcat-ui';
import type { BillingPeriod, PriceDisplay } from '../pricing/catalog';
import styles from './Pricing.module.css';

export function BillingPeriodToggle({
  value,
  onChange,
  className,
}: {
  value: BillingPeriod;
  onChange: (period: BillingPeriod) => void;
  className?: string;
}) {
  return (
    <div className={`${styles.toggle} ${className ?? ''}`} role="radiogroup" aria-label="Billing period">
      {(['monthly', 'annual'] as const).map((period) => (
        <button
          key={period}
          type="button"
          role="radio"
          aria-checked={value === period}
          className={`${styles.toggleButton} ${value === period ? styles.toggleActive : ''}`}
          onClick={() => onChange(period)}
        >
          {period === 'monthly' ? 'Monthly' : 'Annual'}
        </button>
      ))}
    </div>
  );
}

export interface PricingCardPlan {
  name: string;
  description: string;
  features: readonly string[];
  highlight?: boolean;
}

export type PricingCardPrice = Omit<PriceDisplay, 'key' | 'period'> & {
  period: PriceDisplay['period'] | 'forever';
};

export function PricingCard({
  plan,
  price,
  action,
  badge,
  note,
  className,
  style,
}: {
  plan: PricingCardPlan;
  price: PricingCardPrice;
  action: ReactNode;
  badge?: string;
  note?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const headingId = useId();
  return (
    <article
      aria-labelledby={headingId}
      className={`${styles.card} ${plan.highlight ? styles.highlight : ''} ${className ?? ''}`}
      style={style}
    >
      {badge && <span className={styles.badge}>{badge}</span>}
      <h3 id={headingId} className={styles.name}>{plan.name}</h3>
      <p className={styles.description}>{plan.description}</p>
      <p className={styles.priceRow}>
        <span className={styles.price}>{price.amount}</span>
        <span className={styles.period}>{price.period}</span>
      </p>
      {price.annualNote && <p className={styles.annualNote}>{price.annualNote}</p>}
      {price.savingsPercent !== null && (
        <p className={styles.annualSavings}>Save {price.savingsPercent}% vs monthly</p>
      )}
      <ul className={styles.features}>
        {plan.features.map((feature) => (
          <li key={feature}>
            <Icons.CheckIcon size={16} className={styles.check} aria-hidden="true" /> {feature}
          </li>
        ))}
      </ul>
      {note && <p className={styles.note}>{note}</p>}
      <div className={`${styles.action} ${plan.highlight ? styles.primaryAction : ''}`}>
        {action}
      </div>
    </article>
  );
}
